/**
 * ScenarioHeaderChrome.tsx
 * FEAT-2026-0007 Phase 6B.2 — Header chrome for the Scenario Planning wizard.
 * Updated in Phase 6D.1 to wire the Submit Plan button.
 * Updated in Phase 6F.1 to wire the Duplicate button.
 * Updated in Phase 6F.3 to add Draft→NeedsReview→Submitted status workflow.
 *
 * Renders:
 *  - Title row: "Scenario Planning — DRAFT: {Code}, {Name}" + Status chip
 *  - Button row: Submit Plan (live as of 6D.1), Duplicate (live as of 6F.1),
 *                Delete Draft, Back to Dashboard,
 *                ScenarioStatusActions (6F.3: Send for Review / Return to Draft)
 *  - Compare to Baseline toggle
 *  - ScenarioReviewBanner (6F.3): full-width amber band when Status=NeedsReview
 *
 * All colors via theme.schedule.* tokens — no hex literals.
 *
 * All actions are live:
 *   - Back to Dashboard: navigate away
 *   - Compare to Baseline: toggle local boolean
 *   - Autosave note (P2-20, audit #2): the old "Save Draft" button did NO I/O yet
 *     showed "Saving…" and "Draft saved." — a fabricated success that could mask
 *     an earlier failed edit. Every edit already persists per-action through the
 *     individual endpoints, so the button is replaced by a passive
 *     "changes save automatically" indicator that teaches the real model.
 *   - Delete Draft: open ConfirmDialog → deleteScenario + navigate + snackbar
 *   - Submit Plan (6D.1): ConfirmDialog → submitScenario → snackbar + read-only
 *   - Duplicate (6F.1): duplicateScenario → snackbar "Duplicated as {Code}" → switch active
 *   - Send for Review (6F.3): Draft → NeedsReview
 *   - Return to Draft (6F.3): NeedsReview → Draft
 */

import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Chip,
  Snackbar,
  Tooltip,
  Typography,
  Alert,
} from '@mui/material';
import { alpha, useTheme, type Theme } from '@mui/material/styles';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CloudDoneOutlinedIcon from '@mui/icons-material/CloudDoneOutlined';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import LockOpenIcon from '@mui/icons-material/LockOpen';
import { ROUTES } from '../../../../constants/routes';
import {
  convertQuoteToWorkOrder, getQuoteConvertedTarget,
} from '../../../../services/quoteService';

import {
  ConfirmDialog,
  CONFIRM_INITIAL,
  type ConfirmDialogState,
} from '../../../../components/shared/ConfirmDialog';
import type { Scenario, ScenarioStatus, SubmitCheckResult } from '../../../../services/scheduleScenario';
import type { ScheduleReturnTo } from '../../../../types/schedule';
import {
  deleteScenario,
  submitScenario,
  reopenScenario,
  duplicateScenario,
  checkScenarioSubmit,
} from '../../../../services/scheduleScenario';
import { buildSubmitWarnings } from '../../../../components/schedule/submitWarnings';
import { SubmitWarningsContent } from '../../../../components/schedule/SubmitWarningsContent';
import { usePermissions } from '../../../../hooks/usePermissions';

// ---------------------------------------------------------------------------
// Status chip colors — map ScenarioStatus → theme token
// ---------------------------------------------------------------------------

function statusChipSx(status: ScenarioStatus, theme: Theme) {
  switch (status) {
    case 'Draft':
      return {
        backgroundColor: alpha(theme.schedule.tentative, 0.18),
        color: theme.schedule.committed,
        border: `1px solid ${alpha(theme.schedule.committed, 0.3)}`,
      };
    case 'NeedsReview':
      return {
        backgroundColor: alpha(theme.schedule.maintenance, 0.18),
        color: theme.schedule.maintenance,
        border: `1px solid ${alpha(theme.schedule.maintenance, 0.4)}`,
      };
    case 'Submitted':
      return {
        backgroundColor: alpha(theme.schedule.employeeAlloc, 0.18),
        color: theme.schedule.employeeAlloc,
        border: `1px solid ${alpha(theme.schedule.employeeAlloc, 0.4)}`,
      };
    case 'Archived':
      return {
        backgroundColor: alpha(theme.schedule.noActivity, 0.18),
        color: theme.schedule.noActivity,
        border: `1px solid ${alpha(theme.schedule.noActivity, 0.4)}`,
      };
  }
}

function statusLabel(status: ScenarioStatus): string {
  switch (status) {
    case 'Draft':       return 'Draft (tentative)';
    case 'NeedsReview': return 'Needs Review';
    case 'Submitted':   return 'Scheduled';
    case 'Archived':    return 'Archived';
  }
}

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

interface ScenarioHeaderChromeProps {
  scenario: Scenario;
  onScenarioUpdated: (updated: Scenario) => void;
  onScenarioDeleted: () => void;
  onBackToDashboard: () => void;
  /** Called after a successful Duplicate — switches the active scenario to the new copy. */
  onDuplicated?: (newScenario: Scenario) => void;
  /**
   * Deep-link origin (back-nav fix, 2026-07-11): set when the user arrived from
   * a quote's / work order's Add-to-Schedule button. Renders an explicit
   * "Back to {label}" button returning to that page. Absent for dashboard /
   * direct arrivals — nothing extra renders.
   */
  returnTo?: ScheduleReturnTo | null;
}

// ---------------------------------------------------------------------------
// Pre-submit warnings → confirm-dialog body: buildSubmitWarnings moved to the
// shared components/schedule/submitWarnings.ts (G2-1, audit #2) so the source
// commit doors and the Master Gantt bulk commit render the SAME bullets.
// ---------------------------------------------------------------------------

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export const ScenarioHeaderChrome: React.FC<ScenarioHeaderChromeProps> = ({
  scenario,
  onScenarioUpdated,
  onScenarioDeleted,
  onBackToDashboard,
  onDuplicated,
  returnTo,
}) => {
  const theme = useTheme();
  const navigate = useNavigate();
  // Scenario delete is admin-only (matches backend DELETE /scenarios/:key, RBAC audit D3)
  const { isAdmin } = usePermissions();

  // Snackbar state — `action` optionally renders a button (e.g. "View in Master
  // Gantt" after a submit closes the plan→commit→verify loop).
  const [snack, setSnack] = React.useState<{
    open: boolean;
    message: React.ReactNode;
    severity: 'success' | 'info' | 'warning' | 'error';
    action?: React.ReactNode;
  }>({
    open: false,
    message: '',
    severity: 'success',
  });

  // Confirm dialog state
  const [confirm, setConfirm] = React.useState<ConfirmDialogState>(CONFIRM_INITIAL);

  // Loading guards
  const [deleting, setDeleting] = React.useState(false);
  const [submitting, setSubmitting] = React.useState(false);
  const [checking, setChecking] = React.useState(false);
  const [duplicating, setDuplicating] = React.useState(false);
  const [reopening, setReopening] = React.useState(false);

  const showSnack = (message: string, severity: 'success' | 'info' | 'error' = 'success') =>
    setSnack({ open: true, message, severity });

  // Close the plan → commit → verify loop: jump to the live schedule the submit
  // just wrote to. Submitted bookings become Committed (BASELINE), so they show
  // in Master Gantt without a scenario filter.
  const goToLiveSchedule = () => navigate(ROUTES.SCHEDULE_MASTER_GANTT);

  // ── Quote-plan conversion state (2026-08-06 PRJ dup-key incident) ─────────
  // A finalized quote-plan whose conversion FAILED used to strand: the plan is
  // locked ('Scheduled') and the quote page has no obvious door. When this
  // Submitted plan's quote is still unconverted, the header button becomes
  // 'Convert to Work Order' — an in-place retry.
  const [quoteUnconverted, setQuoteUnconverted] = React.useState(false);
  const [retryingConvert, setRetryingConvert] = React.useState(false);
  React.useEffect(() => {
    let cancelled = false;
    if (scenario.Status === 'Submitted' && scenario.SourceType === 'Quote' && scenario.QuoteKey) {
      getQuoteConvertedTarget(scenario.QuoteKey).then((t) => {
        if (!cancelled) setQuoteUnconverted(t === null);
      });
    } else {
      setQuoteUnconverted(false);
    }
    return () => { cancelled = true; };
  }, [scenario.Status, scenario.SourceType, scenario.QuoteKey]);

  const handleRetryConvert = async () => {
    if (!scenario.QuoteKey || retryingConvert) return;
    setRetryingConvert(true);
    try {
      const r = await convertQuoteToWorkOrder(scenario.QuoteKey);
      setSnack({
        open: true,
        severity: 'success',
        message: (
          <>Quote converted — opening Work Order <strong>{r.workOrderId}</strong>…</>
        ),
      });
      setTimeout(() => navigate(
        ROUTES.WORK_ORDER_DETAIL
          .replace(':projectId', String(r.projectId))
          .replace(':workOrderId', r.workOrderKey),
      ), 800);
    } catch (err: unknown) {
      const rec = err as { response?: { data?: { error?: string; code?: string; workOrderKey?: string | null } } };
      if (rec.response?.data?.code === 'QUOTE_ALREADY_CONVERTED') {
        setQuoteUnconverted(false);
        showSnack('This quote is already converted — refreshing state.', 'info');
        return;
      }
      setSnack({
        open: true,
        severity: 'error',
        message: (
          <>The quote could not be converted: <strong>{rec.response?.data?.error ?? 'unknown error'}</strong></>
        ),
      });
    } finally {
      setRetryingConvert(false);
    }
  };

  // ── Delete Draft ───────────────────────────────────────────────────────────
  const handleDeleteDraft = () => {
    setConfirm({
      open: true,
      title: 'Delete Draft Scenario?',
      message: `This will permanently delete scenario ${scenario.Code} — "${scenario.Name}" and release all its tentative bookings and assignments. This action cannot be undone.`,
      confirmLabel: 'Delete Draft',
      confirmColor: 'error',
      onConfirm: async () => {
        setDeleting(true);
        try {
          const result = await deleteScenario(scenario.ScenarioKey);
          // Build a count message so the planner knows what was released
          const parts: string[] = [];
          if (result.bookingsDeleted > 0) {
            parts.push(
              `${result.bookingsDeleted} booking${result.bookingsDeleted !== 1 ? 's' : ''}`,
            );
          }
          if (result.assignmentsDeleted > 0) {
            parts.push(
              `${result.assignmentsDeleted} assignment${result.assignmentsDeleted !== 1 ? 's' : ''}`,
            );
          }
          const countText = parts.length > 0 ? ` — ${parts.join(' and ')} released` : '';
          showSnack(`Draft deleted${countText}.`);
          // Give the snackbar a moment, then navigate away
          setTimeout(() => onScenarioDeleted(), 400);
        } catch (err: unknown) {
          const msg =
            err &&
            typeof err === 'object' &&
            'response' in err
              ? (err as { response?: { data?: { error?: string } } }).response?.data?.error
              : undefined;
          showSnack(
            msg ?? 'Failed to delete scenario. Submitted scenarios cannot be deleted.',
            'error',
          );
        } finally {
          setDeleting(false);
        }
      },
    });
  };

  // ── Submit Plan (6D.1) — with pre-submit safety check ─────────────────────
  const handleCommitSubmit = async () => {
    if (submitting || checking) return;
    // Guard: already Submitted — nothing to do
    if (scenario.Status === 'Submitted') {
      showSnack('This plan has already been submitted.', 'info');
      return;
    }

    // Run the read-only pre-submit check first. If it fails (transport/auth),
    // fall back to the plain confirm rather than blocking submit on a check error.
    // `warnings` (the plain-text builder) stays the "does this warrant a warn
    // dialog?" decision; the dialog body renders the STRUCTURED sections
    // (SubmitWarningsContent, 2026-08-05) from the same check result.
    let warnings: string | null = null;
    let warnCheck: SubmitCheckResult | null = null;
    setChecking(true);
    try {
      const check = await checkScenarioSubmit(scenario.ScenarioKey);
      warnings = buildSubmitWarnings(check);
      if (warnings) warnCheck = check;
    } catch {
      warnings = null; // check unavailable — proceed with the standard confirm
    } finally {
      setChecking(false);
    }

    // W2-6: state this button's scope explicitly — it submits the WHOLE plan.
    // Quote-sourced plans (2026-08-06, "finalize = won"): finalizing ALSO
    // marks the quote Accepted and converts it into a real Work Order, so the
    // dialog must say exactly that before the user commits.
    const isQuotePlan = scenario.SourceType === 'Quote' && !!scenario.QuoteKey;
    const baseMessage = isQuotePlan
      ? 'Finalizing this plan does all of the following in one step:'
      : `Submits this whole plan — every job (quote or work order) planned in ` +
        `"${scenario.Code} — ${scenario.Name}" has its tentative bookings and assignments ` +
        `committed to the confirmed live schedule, and the plan is locked ` +
        `(use "Reopen to Edit" to change it later).`;
    // Names the user must recognize, each on its own line and bold — the old
    // paragraph buried the plan / quote / client identity (Vahid 2026-08-06).
    const quotePlanBody = isQuotePlan ? (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75, mt: 0.5 }}>
        <Typography variant="body2">
          Plan <strong>{scenario.Code}</strong> — <strong>{scenario.Name}</strong>
        </Typography>
        <Typography variant="body2">
          Quote <strong>{scenario.QuoteNumber ?? '—'}</strong>
          {scenario.ClientName ? <> · Client <strong>{scenario.ClientName}</strong></> : null}
        </Typography>
        <Typography variant="body2" component="div">
          <ul style={{ margin: '4px 0 0', paddingLeft: 20 }}>
            <li>Commits every tentative booking and assignment to the live schedule</li>
            <li>Marks the quote <strong>ACCEPTED</strong></li>
            <li>Creates the <strong>Work Order</strong> (samples arrive later via Add Samples)</li>
            <li>Locks the plan (&ldquo;Reopen to Edit&rdquo; to change it)</li>
          </ul>
        </Typography>
      </Box>
    ) : null;

    setConfirm({
      open: true,
      title: warnings
        ? (isQuotePlan ? 'Finalize & convert this quote anyway?' : 'Finalize this plan anyway?')
        : (isQuotePlan ? 'Finalize & convert to Work Order?' : 'Finalize this plan?'),
      message: baseMessage,
      body: (quotePlanBody || warnCheck) ? (
        <>
          {quotePlanBody}
          {warnCheck ? <SubmitWarningsContent check={warnCheck} /> : null}
        </>
      ) : undefined,
      confirmLabel: warnings
        ? (isQuotePlan ? 'Finalize & Convert anyway' : 'Finalize anyway')
        : (isQuotePlan ? 'Finalize & Convert' : 'Finalize Schedule'),
      confirmColor: warnings ? 'warning' : 'primary',
      onConfirm: async () => {
        setSubmitting(true);
        try {
          const result = await submitScenario(scenario.ScenarioKey);
          // Build confirmation message with counts
          const parts: string[] = [];
          if (result.bookingsCommitted > 0) {
            parts.push(`${result.bookingsCommitted} booking${result.bookingsCommitted !== 1 ? 's' : ''}`);
          }
          if (result.assignmentsCommitted > 0) {
            parts.push(
              `${result.assignmentsCommitted} assignment${result.assignmentsCommitted !== 1 ? 's' : ''}`,
            );
          }
          const countText = parts.length > 0 ? ` — ${parts.join(' and ')} committed` : '';
          // Quote-sourced plans: the server converted the quote into a Work
          // Order as part of finalize — land the planner on the new WO.
          const conv = result.conversion;
          if (conv?.workOrderKey && conv.projectId) {
            setSnack({
              open: true,
              message: (
                <>Plan finalized{countText}. Quote converted — opening Work Order{' '}
                  <strong>{conv.workOrderId ?? ''}</strong>…</>
              ),
              severity: 'success',
            });
            onScenarioUpdated({ ...scenario, Status: 'Submitted' });
            setTimeout(() => navigate(
              ROUTES.WORK_ORDER_DETAIL
                .replace(':projectId', String(conv.projectId))
                .replace(':workOrderId', String(conv.workOrderKey)),
            ), 900);
            return;
          }
          if (conv?.error) {
            setQuoteUnconverted(true);
            setSnack({
              open: true,
              message: (
                <>Plan finalized{countText}, but the quote could not be converted:{' '}
                  <strong>{conv.error}</strong> — use the{' '}
                  <strong>Convert to Work Order</strong> button (top of the page) to retry.</>
              ),
              severity: 'warning',
            });
          } else {
            // Success snackbar carries a "View in Master Gantt" action so the planner
            // can jump straight to the live schedule it just committed.
            setSnack({
              open: true,
              message: `Plan submitted${countText}.`,
              severity: 'success',
              action: (
                <Button color="inherit" size="small" onClick={goToLiveSchedule} startIcon={<OpenInNewIcon />}>
                  View in Master Gantt
                </Button>
              ),
            });
          }

          // Notify parent with updated scenario (Status → Submitted)
          const updatedScenario: Scenario = { ...scenario, Status: 'Submitted' };
          onScenarioUpdated(updatedScenario);
        } catch (err: unknown) {
          const msg =
            err &&
            typeof err === 'object' &&
            'response' in err
              ? (err as { response?: { data?: { error?: string } } }).response?.data?.error
              : undefined;
          showSnack(
            msg ?? 'Failed to submit plan. Please try again.',
            'error',
          );
        } finally {
          setSubmitting(false);
        }
      },
    });
  };

  // ── Reopen (2026-06-25) — un-submit a scheduled plan so it can be edited ───
  // A scheduled plan is locked server-side (scenarioEditGuard); Reopen flips its rows
  // back to tentative (Status→Draft). Because every booking/assignment status changes,
  // we hard-reload after success so all panels show the fresh editable Draft state.
  const handleReopen = () => {
    setConfirm({
      open: true,
      title: 'Reopen this scheduled plan?',
      message:
        `This puts ${scenario.Code} — "${scenario.Name}" back into Draft so you can change machines, ` +
        `dates, splits, or staffing. Its bookings and assignments return to tentative and drop off the ` +
        `live schedule until you Finalize it again. (To keep this plan as-is and explore a change, use ` +
        `Duplicate instead.)`,
      confirmLabel: 'Reopen to Edit',
      confirmColor: 'primary',
      onConfirm: async () => {
        setReopening(true);
        try {
          await reopenScenario(scenario.ScenarioKey);
          showSnack('Plan reopened — back to Draft. Reloading…');
          // Hard reload: the scenarioKey is in the URL, so this re-resolves the now-Draft
          // plan and refetches every panel's bookings/assignments in their new state.
          setTimeout(() => window.location.reload(), 600);
        } catch (err: unknown) {
          const msg =
            err && typeof err === 'object' && 'response' in err
              ? (err as { response?: { data?: { error?: string } } }).response?.data?.error
              : undefined;
          showSnack(msg ?? 'Failed to reopen plan. Please try again.', 'error');
          setReopening(false);
        }
      },
    });
  };

  // ── Duplicate (6F.1) ─────────────────────────────────────────────────────
  const handleDuplicate = async () => {
    if (duplicating) return;
    setDuplicating(true);
    try {
      const result = await duplicateScenario(scenario.ScenarioKey);
      showSnack(`Duplicated as ${result.scenario.Code}`);
      // Switch the active scenario to the new copy so the planner is now editing it
      if (onDuplicated) {
        onDuplicated(result.scenario);
      } else {
        onScenarioUpdated(result.scenario);
      }
    } catch (err: unknown) {
      const msg =
        err &&
        typeof err === 'object' &&
        'response' in err
          ? (err as { response?: { data?: { error?: string } } }).response?.data?.error
          : undefined;
      showSnack(msg ?? 'Failed to duplicate scenario. Please try again.', 'error');
    } finally {
      setDuplicating(false);
    }
  };

  const isDeleteable = (scenario.Status === 'Draft' || scenario.Status === 'NeedsReview') && isAdmin;

  // 2026-07-10 scenario-naming fix: name the JOB this plan is for right in the
  // header, so landing on a plan (deep-link, dashboard open) identifies it —
  // "Terrane — Q-2026-9065 · TestNewClient". Only rendered when the backend's
  // joined source fields are present (single-scenario reads carry them; a
  // just-created row's POST response doesn't — its Name already names the job).

  return (
    // ONE-row header (2026-07-26 items 3+4): title + status chip + every plan
    // action + the exits share a single wrapping row — the "Planning for
    // quote…" subline (redundant with the title) and the Compare-to-Baseline
    // toggle are gone. No other action was removed.
    <Box sx={{ mb: 1, pb: 0.75, borderBottom: `1px solid ${theme.palette.divider}` }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
        <Typography variant="subtitle1" fontWeight={700} color="text.primary" sx={{ lineHeight: 1.25 }}>
          Scenario Planning
          <Box component="span" sx={{ fontWeight: 400, color: 'text.secondary' }}>
            {scenario.Status === 'Submitted' ? ' — SUBMITTED: ' : ' — DRAFT: '}
          </Box>
          {scenario.Code}, {scenario.Name}
        </Typography>

        {/* Status chip */}
        <Chip
          label={statusLabel(scenario.Status)}
          size="small"
          sx={{
            fontWeight: 600,
            fontSize: '0.72rem',
            height: 22,
            ...statusChipSx(scenario.Status, theme),
          }}
        />
        {/* Finalize Schedule (6D.1) — turns this tentative plan into the committed live schedule */}
        <Tooltip
          title={
            scenario.Status === 'Submitted'
              ? (quoteUnconverted
                ? 'The schedule is committed, but the quote is NOT converted yet — click to create the Work Order now'
                : 'This plan is finalized — its bookings and assignments are on the live schedule')
              : scenario.SourceType === 'Quote'
                ? 'Finalize this plan AND convert the quote — commits the schedule, marks the quote Accepted, and creates the Work Order'
                : 'Finalize this plan — turns every tentative booking and assignment into the confirmed live schedule'
          }
          arrow
        >
          <span>
            <Button
              variant="contained"
              color={quoteUnconverted && scenario.Status === 'Submitted' ? 'warning' : 'primary'}
              size="small"
              startIcon={<CheckCircleOutlineIcon />}
              onClick={quoteUnconverted && scenario.Status === 'Submitted' ? handleRetryConvert : handleCommitSubmit}
              disabled={submitting || checking || retryingConvert
                || (scenario.Status === 'Submitted' && !quoteUnconverted)
                || scenario.Status === 'Archived'}
              sx={{ textTransform: 'none', fontWeight: 600 }}
            >
              {checking
                ? 'Checking…'
                : submitting
                  ? 'Finalizing…'
                  : retryingConvert
                    ? 'Converting…'
                    : scenario.Status === 'Submitted'
                      ? (quoteUnconverted ? 'Convert to Work Order' : 'Scheduled')
                      : scenario.SourceType === 'Quote'
                        ? 'Finalize & Convert to Work Order'
                        : 'Finalize Schedule'}
            </Button>
          </span>
        </Tooltip>

        {/* Once submitted, give a persistent path to the live schedule this plan
            wrote — the wizard used to dead-end here (UX audit 2026-06-20). */}
        {scenario.Status === 'Submitted' && (
          <Tooltip title="Open the live Master Gantt to verify the committed work" arrow>
            <Button
              variant="contained"
              color="primary"
              size="small"
              startIcon={<OpenInNewIcon />}
              onClick={goToLiveSchedule}
              sx={{ textTransform: 'none', fontWeight: 600 }}
            >
              View in Master Gantt
            </Button>
          </Tooltip>
        )}

        {/* Reopen (2026-06-25) — a scheduled plan is locked against edits; this un-submits
            it back to a Draft so machines / dates / splits / staffing can be changed. */}
        {scenario.Status === 'Submitted' && (
          <Tooltip title="Put this scheduled plan back into Draft so you can edit it (un-commits its bookings until you Finalize again)" arrow>
            <span>
              <Button
                variant="outlined"
                color="primary"
                size="small"
                startIcon={<LockOpenIcon />}
                onClick={handleReopen}
                disabled={reopening}
                sx={{ textTransform: 'none', fontWeight: 600 }}
              >
                {reopening ? 'Reopening…' : 'Reopen to Edit'}
              </Button>
            </span>
          </Tooltip>
        )}

        {/* P2-20 (audit #2): honest UI — the old "Save Draft" button performed no
            I/O yet reported "Draft saved.", masking any earlier silent write
            failure. Edits persist per-action (each task/booking/staffing endpoint
            saves as the planner works), so a passive indicator teaches the real
            autosave model instead of fabricating a success signal. */}
        {scenario.Status !== 'Submitted' && scenario.Status !== 'Archived' && (
          <Tooltip
            title="Every edit in this plan is saved to the server as you make it — there is no separate save step"
            arrow
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, px: 0.5 }}>
              <CloudDoneOutlinedIcon sx={{ fontSize: '1rem', color: 'text.secondary' }} />
              <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
                Changes save automatically
              </Typography>
            </Box>
          </Tooltip>
        )}

        {/* Duplicate (6F.1) */}
        <Tooltip title="Clone this scenario — creates a new Draft with all bookings and assignments copied" arrow>
          <span>
            <Button
              variant="outlined"
              size="small"
              startIcon={<ContentCopyIcon />}
              onClick={() => { void handleDuplicate(); }}
              disabled={duplicating}
              sx={{ textTransform: 'none' }}
            >
              {duplicating ? 'Duplicating…' : 'Duplicate'}
            </Button>
          </span>
        </Tooltip>

        {/* Delete Draft */}
        <Tooltip
          title={isDeleteable
            ? 'Permanently delete this draft scenario'
            : !isAdmin
              ? 'Deleting scenarios requires a Lab Admin'
              : 'Submitted scenarios cannot be deleted'}
          arrow
        >
          <span>
            <Button
              variant="outlined"
              color="error"
              size="small"
              startIcon={<DeleteOutlineIcon />}
              onClick={handleDeleteDraft}
              disabled={!isDeleteable || deleting}
              sx={{ textTransform: 'none' }}
            >
              {deleting ? 'Deleting…' : 'Delete Draft'}
            </Button>
          </span>
        </Tooltip>

        {/* Spacer */}
        <Box sx={{ flex: 1 }} />

        {/* Exits — quiet, at the row's end. */}
        {returnTo && (
          <Tooltip title={`Return to ${returnTo.label} — the page you came from`} arrow>
            <Button
              variant="outlined"
              size="small"
              startIcon={<ArrowBackIcon />}
              onClick={() => navigate(returnTo.path)}
              sx={{ textTransform: 'none', fontWeight: 600 }}
            >
              Back to {returnTo.label}
            </Button>
          </Tooltip>
        )}
        <Button
          variant="outlined"
          size="small"
          startIcon={<ArrowBackIcon />}
          onClick={onBackToDashboard}
          sx={{ textTransform: 'none', fontWeight: 600 }}
        >
          Back to Dashboard
        </Button>
      </Box>

      {/* ── Snackbar ───────────────────────────────────────────────────────── */}
      <Snackbar
        open={snack.open}
        autoHideDuration={snack.action ? 8000 : 4000}
        onClose={() => setSnack((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          severity={snack.severity}
          onClose={() => setSnack((s) => ({ ...s, open: false }))}
          action={snack.action}
          sx={{ width: '100%' }}
        >
          {snack.message}
        </Alert>
      </Snackbar>

      {/* ── Confirm dialog (Delete Draft) ──────────────────────────────────── */}
      <ConfirmDialog
        state={confirm}
        onClose={() => setConfirm((s) => ({ ...s, open: false }))}
      />
    </Box>
  );
};
